import { type LucideIcon, LayoutDashboard, ShoppingBag, Package, Users, Tag, BarChart2, Megaphone,
  Palette, Settings, Archive, FileText, Truck, Percent, Image, Star, Mail, RotateCcw,
  MapPin, List, Cpu, Shield, MessageSquare, Smartphone, CreditCard, Ban, UserCog, StickyNote, Layers } from 'lucide-react'

import { sidebarConfig, type SidebarItem } from '@/config/sidebar'

export interface NavItem {
  label: string
  route: string
  icon: LucideIcon
  keywords: string[]
}

/** Sidebar link labels by route, so the palette names a page the way the sidebar does. */
const sidebarLabels = new Map<string, string>()
const collectLabels = (items: SidebarItem[]) => {
  for (const item of items) {
    if (item.type === 'link' && item.to && item.label) sidebarLabels.set(item.to, item.label)
    if (item.children) collectLabels(item.children)
  }
}
collectLabels(sidebarConfig)

/**
 * `label` is set only for pages the sidebar doesn't list, or where its label needs context in the palette
 * (`/settings` is "General" in the settings nav); every other label comes from `config/sidebar.tsx`.
 */
const PAGES: (Omit<NavItem, 'label'> & { label?: string })[] = [
  { route: '/',                        icon: LayoutDashboard, keywords: ['home', 'dashboard', 'overview'] },
  { route: '/orders',                  icon: ShoppingBag,     keywords: ['order', 'sale', 'purchase'] },
  { route: '/orders/new',              icon: ShoppingBag,     keywords: ['new order', 'create order', 'manual order'], label: 'Create order' },
  { route: '/products',                icon: Package,         keywords: ['product', 'item', 'sku', 'inventory'] },
  { route: '/products/new',            icon: Package,         keywords: ['add product', 'new product'], label: 'Add product' },
  { route: '/customers',               icon: Users,           keywords: ['customer', 'buyer', 'client', 'user'] },
  { route: '/coupons',                 icon: Tag,             keywords: ['coupon', 'discount', 'promo', 'code'] },
  { route: '/analytics',               icon: BarChart2,       keywords: ['analytics', 'report', 'stats', 'revenue', 'chart'] },
  { route: '/reports/sales',           icon: FileText,        keywords: ['report', 'sales', 'orders', 'export'] },
  { route: '/reports/revenue',         icon: FileText,        keywords: ['report', 'revenue', 'income', 'export'] },
  { route: '/reports/customers',       icon: FileText,        keywords: ['report', 'customer', 'buyers', 'export'] },
  { route: '/reports/inventory',       icon: FileText,        keywords: ['report', 'inventory', 'stock', 'export'] },
  { route: '/campaigns',               icon: Megaphone,       keywords: ['campaign', 'banner', 'promotion', 'marketing'] },
  { route: '/themes',                  icon: Palette,         keywords: ['theme', 'builder', 'design', 'appearance', 'style', 'online store'] },
  { route: '/settings',                icon: Settings,        keywords: ['setting', 'config', 'general'], label: 'Settings' },
  { route: '/inventory',               icon: Archive,         keywords: ['inventory', 'stock', 'warehouse'] },
  { route: '/blog',                    icon: FileText,        keywords: ['blog', 'post', 'article', 'content'] },
  { route: '/shipping',                icon: Truck,           keywords: ['shipping', 'delivery', 'courier'] },
  { route: '/taxes',                   icon: Percent,         keywords: ['tax', 'vat', 'gst'] },
  { route: '/media',                   icon: Image,           keywords: ['media', 'library', 'image', 'file', 'upload'] },
  { route: '/reviews',                 icon: Star,            keywords: ['review', 'rating', 'feedback'] },
  { route: '/marketing',               icon: Mail,            keywords: ['email', 'marketing', 'newsletter'] },
  { route: '/returns',                 icon: RotateCcw,       keywords: ['return', 'refund', 'exchange'] },
  { route: '/categories',              icon: List,            keywords: ['category', 'collection'] },
  { route: '/brands',                  icon: Cpu,             keywords: ['brand', 'manufacturer'] },
  { route: '/attributes',              icon: Layers,          keywords: ['attribute', 'variant', 'option', 'size', 'colour'] },
  { route: '/orders/methods',          icon: Truck,           keywords: ['delivery method', 'shipping method'] },
  { route: '/orders/locations',        icon: MapPin,          keywords: ['delivery zone', 'location', 'area'] },
  { route: '/orders/sources',          icon: ShoppingBag,     keywords: ['order source', 'channel'] },
  { route: '/orders/notes',            icon: StickyNote,      keywords: ['order note', 'note'] },
  { route: '/system/activity-log',     icon: List,            keywords: ['log', 'activity', 'audit'] },
  { route: '/system/chat-sessions',    icon: MessageSquare,   keywords: ['chat', 'session', 'support'], label: 'Chat sessions' },
  { route: '/system/sms-gateways',     icon: Smartphone,      keywords: ['sms', 'gateway', 'notification'] },
  { route: '/system/payment-gateways', icon: CreditCard,      keywords: ['payment', 'gateway', 'ssl', 'bkash', 'nagad'] },
  { route: '/system/ip-blocklist',     icon: Ban,             keywords: ['ip', 'block', 'blocklist', 'ban', 'security'] },
  { route: '/system/roles',            icon: Shield,          keywords: ['role', 'permission', 'access'] },
  { route: '/system/administration',   icon: UserCog,         keywords: ['admin', 'administration', 'staff', 'users'] },
  { route: '/profile',                 icon: UserCog,         keywords: ['profile', 'password', 'account'], label: 'Profile' },
  { route: '/plugins',                 icon: Cpu,             keywords: ['plugin', 'extension', 'app', 'integration'] },
]

export const NAV_ITEMS: NavItem[] = PAGES.map(({ label, ...page }) => ({
  ...page,
  label: label ?? sidebarLabels.get(page.route) ?? page.route,
}))

/** Routes the sidebar shows (after feature flags); `to` of every link, nested groups included. */
export function sidebarRoutes(items: { to?: string, children?: { to?: string, children?: unknown[] }[] }[]): Set<string> {
  const routes = new Set<string>()
  const walk = (list: { to?: string, children?: unknown[] }[]) => {
    for (const item of list) {
      if (item.to) routes.add(item.to)
      if (Array.isArray(item.children)) walk(item.children as { to?: string, children?: unknown[] }[])
    }
  }
  walk(items)
  return routes
}

/** A page is offered when the sidebar shows it or the section it belongs to (/orders/new → /orders). */
const isVisible = (route: string, visible?: Set<string>): boolean => {
  if (!visible || route === '/profile') return true
  if (visible.has(route)) return true
  const section = `/${route.split('/')[1] ?? ''}`
  return section !== '/' && visible.has(section)
}

/**
 * Pages matching the query: every word must appear in the label or a keyword; labels that start with the
 * query rank first. `visible` (from sidebarRoutes) hides pages switched off by feature flags.
 */
export function filterNav(query: string, visible?: Set<string>): NavItem[] {
  const q = query.toLowerCase().trim()
  if (!q) return []
  const words = q.split(/\s+/)
  const haystack = (item: NavItem) => [item.label.toLowerCase(), ...item.keywords].join(' ')
  return NAV_ITEMS
    .filter((item) => isVisible(item.route, visible) && words.every((w) => haystack(item).includes(w)))
    .sort((a, b) => Number(!a.label.toLowerCase().startsWith(q)) - Number(!b.label.toLowerCase().startsWith(q)))
    .slice(0, 6)
}
